import * as Sentry from '@sentry/react';
import LargeFileUploadError from './LargeFileUploadError';

export const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10 MB
export const LARGE_FILE_MAX_SIZE = 100 * 1024 * 1024; // 100 MB

class UnexpectedFileUploaderStateError extends Error {}

export default abstract class AbstractLargeFileUploader<UploadResponse> {
  protected abstract readonly CHUNK_SIZE: number;

  async uploadLargeFile(
    file: File,
    onProgressUpdate: (progress: number) => void,
  ): Promise<UploadResponse> {
    try {
      const uploadChunkApiUrl = await this.startUploadSession(file);

      onProgressUpdate(0);

      const chunkCount = Math.ceil(file.size / this.CHUNK_SIZE);

      for (let i = 0; i < chunkCount; i++) {
        const chunkByteRange = [
          i * this.CHUNK_SIZE,
          (i + 1) * this.CHUNK_SIZE,
        ] as [number, number];
        const chunk = file.slice(chunkByteRange[0], chunkByteRange[1]);
        const isLastChunk = i === chunkCount - 1;

        if (isLastChunk) {
          const [uploadComplete, uploadResponse] = await this.uploadFileChunk(
            uploadChunkApiUrl,
            chunk,
            chunkByteRange,
            file.size,
          );

          if (!uploadComplete) {
            throw new LargeFileUploadError('Could not complete file upload.');
          }

          onProgressUpdate(100);

          return uploadResponse;
        }

        await this.uploadFileChunk(
          uploadChunkApiUrl,
          chunk,
          chunkByteRange,
          file.size,
        );

        const uploadProgress = Math.round(((i + 1) / chunkCount) * 100);

        onProgressUpdate(uploadProgress);
      }
    } catch (error) {
      Sentry.captureException(error);

      if (error instanceof LargeFileUploadError) {
        throw error;
      }

      throw new LargeFileUploadError();
    }

    throw new UnexpectedFileUploaderStateError();
  }

  protected abstract startUploadSession(file: File): Promise<string>;

  protected abstract uploadFileChunk(
    uploadChunkApiUrl: string,
    fileChunk: Blob,
    chunkByteRange: [number, number],
    totalFileSize: number,
  ): Promise<[false, unknown] | [true, UploadResponse]>;

  protected getStandardUploadChunkRequestHeaders(
    fileChunk: Blob,
    chunkByteRange: [number, number],
    totalFileSize: number,
  ): Record<string, string> {
    return {
      'Content-Length': fileChunk.size.toString(),
      'Content-Range': `bytes ${chunkByteRange[0]}-${Math.min(chunkByteRange[1] - 1, totalFileSize - 1)}/${totalFileSize}`,
    };
  }
}
